import React, { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useState, useEffect, forwardRef } from "react";

import { toast, ToastContainer } from "react-toastify";


import {
  getMasterResourceListURL,
  serviceBaseURL,
  appserveraddr,
  dmscoreservicecontextname,
  deleteMasterResourceById,
  deleteMasterResourceImageByName,
  getincidentcatcreate,
  getThemeURL,
  deleteincident,
  getTaskConfigsURL,
} from "../../../Services/EndPoints";


import Moment from "react-moment";
import {
  masterResourceTableData,
  masterResourcecolumnsForExport,
} from "../../../JsonData/MasterResourceConfig";
import { Link } from "react-router-dom";

import Loader from "react-spinner-loader";
import { useTranslation } from "react-i18next";
import Confirmpopup from "../Common/ConfirmPopup/Confirmpopup";
import { getExportedColumnsData } from "../../../Components/UiComponents/utils";
import { Deleteicon, Editicon, MapIcon, Viewicon } from "../../../Components/IconComponents";


import { deleteDataById, getData, postDataWithBody } from "../../../Services/Services";
import { setTableData } from "../../../CentralStore/ExportLMSTable/dataSlice";
import IncidentShowTableData from "../../../Components/UiComponents/Table/IncidentShowTableData";
import ExportExcelButton from "../../../Components/UiComponents/Table/TableComponents/ExportExcelButton/ExportExcelButton";
import { RoleContext } from "../../..";
import { ListChecks, AlertOctagon, AlertTriangle, CalendarClock, XCircle, Filter as FilterIcon } from "lucide-react";

// Fixed enumeration — matches the Severity dropdown used everywhere else
// (Incident Category Matrix, SOP forms, etc.), not derived from live data.
const SEVERITY_OPTIONS = ["Level 1 – Minor", "Level 2 – Moderate", "Level 3 – Major", "Level 4 – Critical"];

// Fixed enumeration of the incident lifecycle stages (current_stage) — not
// derived from live data, so every stage is always selectable in the filter.
const STAGE_OPTIONS = ["ALERT_GENERATED", "INCIDENT_CREATED", "SOP", "CLOSED", "CANCELLED"];
const STAGE_LABELS = {
  ALERT_GENERATED: "Alert Generated",
  INCIDENT_CREATED: "Incident Created",
  SOP: "SOP Execution",
  CLOSED: "Incident Closed",
  CANCELLED: "Incident Cancelled",
};

const filterLabelStyle = {
  fontSize: "9.5px",
  fontWeight: 800,
  letterSpacing: ".3px",
  textTransform: "uppercase",
  color: "#98A2B3",
};

const filterControlStyle = {
  padding: "6px 10px",
  borderRadius: "8px",
  border: "1px solid #E4E7EC",
  fontSize: "12px",
  background: "#fff",
  color: "#1D2939",
};

// Light color palette per stage/status keyword — order matters, since e.g.
// "AUTO_SOP_FAILED" contains "sop" too, so the more specific "fail" rule
// must be checked before the generic "sop" one.
const STAGE_COLOR_RULES = [
  { test: /cancel/i, bg: "#FEE2E2", color: "#EF4444", dot: "#F87171" },
  { test: /fail/i, bg: "#FEF2F2", color: "#F87171", dot: "#FCA5A5" },
  { test: /clos/i, bg: "#DCFCE7", color: "#4ADE80", dot: "#86EFAC" },
  { test: /complet/i, bg: "#DCFCE7", color: "#4ADE80", dot: "#86EFAC" },
  { test: /progress/i, bg: "#FEF3C7", color: "#FBBF24", dot: "#FCD34D" },
  { test: /deploy/i, bg: "#DBEAFE", color: "#60A5FA", dot: "#93C5FD" },
  { test: /releas/i, bg: "#E0F2FE", color: "#38BDF8", dot: "#7DD3FC" },
  { test: /creat/i, bg: "#FFF7ED", color: "#FB923C", dot: "#FDBA74" },
  { test: /sop/i, bg: "#EDE9FE", color: "#A78BFA", dot: "#C4B5FD" },
];
const getStageColors = (value) =>
  STAGE_COLOR_RULES.find((r) => r.test.test(value || "")) || { bg: "#F3F4F6", color: "#9CA3AF", dot: "#D1D5DB" };

// Mirrors the "DD-MM-YYYY hh:mm A" format the table's Moment cell displays,
// so searching by date matches what's actually shown on screen.
const formatSearchableDate = (value) => {
  const d = new Date(value);
  if (isNaN(d.getTime())) return String(value);
  const pad = (n) => String(n).padStart(2, "0");
  const hours12 = d.getHours() % 12 || 12;
  const ampm = d.getHours() < 12 ? "AM" : "PM";
  return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()} ${pad(hours12)}:${pad(d.getMinutes())} ${ampm}`;
};

// "CANCELLED" -> "Cancelled" — only the first letter capitalized, not each word.
const toSentenceCase = (value) => {
  if ((value || "").toUpperCase() === "SOP") return "SOP";
  const s = (value || "").replace(/_/g, " ").toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
};

// Derived purely from the Incident List table response — no extra API calls.
const INCIDENT_KPI_CARDS = [
  {
    key: "total",
    label: "Total Incidents",
    icon: ListChecks,
    bg: "#EBF4FF",
    color: "#3579D4",
    compute: (rows) => rows.length,
  },
  {
    key: "critical",
    label: "Critical",
    icon: AlertOctagon,
    bg: "#FFF0F0",
    color: "#D95555",
    compute: (rows) =>
      rows.filter((r) => (r.severity || "").toLowerCase().includes("critical")).length,
  },
  {
    key: "major",
    label: "Major",
    icon: AlertTriangle,
    bg: "#FFF4EA",
    color: "#D4772A",
    compute: (rows) =>
      rows.filter((r) => (r.severity || "").toLowerCase().includes("major")).length,
  },
  {
    key: "today",
    label: "Today's Incidents",
    icon: CalendarClock,
    bg: "#EEFAF3",
    color: "#27A05A",
    compute: (rows) => {
      const today = new Date();
      return rows.filter((r) => {
        if (!r.created_on) return false;
        const d = new Date(r.created_on);
        return (
          d.getFullYear() === today.getFullYear() &&
          d.getMonth() === today.getMonth() &&
          d.getDate() === today.getDate()
        );
      }).length;
    },
  },
  {
    key: "cancelled",
    label: "Incident Cancelled",
    icon: XCircle,
    bg: "#F3F4F6",
    color: "#6B7280",
    compute: (rows) =>
      rows.filter((r) => (r.current_status || "").toUpperCase() === "INCIDENT_CANCELLED").length,
  },
];

// Matches the visible Incident List columns (ID, Incident Name, Incident
// Category, Incident Description, Severity, Current Stage, Incident
// Date & Time) for the Excel export.
const incidentExportColumns = [
  { key: "incident_seq_id", label: "Incident ID" },
  { key: "name", label: "Incident Name" },
  { key: "category", label: "Incident Category" },
  { key: "description", label: "Incident Description" },
  { key: "severity", label: "Severity" },
  { key: "current_stage", label: "Current Stage" },
  { key: "current_status", label: "Current Status" },
  { key: "disaster_notification_seq_id", label: "Disaster Alert ID" },
  { key: "created_by", label: "Incident Created By" },
  { key: "created_on", label: "Incident Date & Time" },
  { key: "closed_by", label: "Incident Closed By" },
  { key: "closed_by_email", label: "Closed By Email" },
  { key: "closed_by_mobile", label: "Closed By Mobile Number" },
  { key: "closed_on", label: "Incident Closed On" },
];

const IncidentTable = ({ onRowClick, selectedRowId, selectedIncidentSeqId, initialSearch = "" }) => {
  const { roleName } = useContext(RoleContext);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [data, setData] = useState([]);
  const [loader, setLoader] = useState(false);
  const qs = require("qs");
  const [masterResourceData, setMasterResourceData] = useState(
    masterResourceTableData
  );
  const [searchData, setSearchData] = useState([]);
  const [stateId, setStateId] = useState(null);
  const [statename, setStatename] = useState(null);
  const [searchText, setSearchText] = useState(initialSearch);
  const [severityFilter, setSeverityFilter] = useState("All");
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [stageFilter, setStageFilter] = useState("All");
  // Canonical Incident Category list (same source as Incident Category
  // Matrix's own category dropdown) so the filter always offers every
  // configured category, not just whichever ones already have incidents.
  const [masterCategoryOptions, setMasterCategoryOptions] = useState([]);


  const { t, i18n } = useTranslation();
  useEffect(() => {
  if (stateId) {
    getMasterResourceList();
  }
  }, [stateId]);

  useEffect(() => {
    if (!stateId) return;
    const fetchCategoryOptions = async () => {
      try {
        const res = await postDataWithBody(`${getTaskConfigsURL}/${stateId}`, ["INCDNT_CAT"], {});
        const options = Array.isArray(res) ? res.map((config) => config.value).filter(Boolean) : [];
        setMasterCategoryOptions(options.sort());
      } catch (error) {
        console.error("Error fetching incident category options:", error);
      }
    };
    fetchCategoryOptions();
  }, [stateId]);


  useEffect(() => {
    sendDataToCentralStore(searchData);
  }, [searchData]);

  
    const getUserProfile = async () => {
    try {
      const profile = await getData(getThemeURL);
      if (profile && profile.length > 0) {
        setStateId(profile[0].state_id);
        setStatename(profile[0].statename)
      }
    } catch (error) {
      console.error("Error fetching user profile:", error);
    }
  };

   useEffect(() => {
      getUserProfile();
    }, []);

  // When arriving from View Incident → Quick Links → "Incident Closed", pre-fill
  // the search box with that incident's name so the list filters straight to it.
  useEffect(() => {
    if (initialSearch) setSearchText(initialSearch);
  }, [initialSearch]);

  async function getMasterResourceList() {
    setLoader(true);

    let masterResourceList = await getData(getincidentcatcreate.replace("STATEID", stateId));

    let sortedList = masterResourceList.sort((a, b) => {
      return new Date(b.created_on) - new Date(a.created_on);
    });
    setLoader(false);

    setData(sortedList);
  }
  const sendDataToCentralStore = (data) => {
    if (data.length > 0) {
      const columns = masterResourcecolumnsForExport;
      dispatch(
        setTableData({
          columns,
          tableData: getExportedColumnsData(columns, data),
        })
      );
    }
  };
  const addNewHandler = () => {
    navigate("/situation-awareness/create-incidentpage");
  };

  const getImageWithAuth = async (imageName) => {
    const imageUrl = `${appserveraddr}/${dmscoreservicecontextname}/master_resource/get_resource_image/${imageName}`;

    try {
      const response = await fetch(imageUrl, {
        headers: {
          Authorization: 'Bearer ' + localStorage.getItem("auth_token"),
        },
      });

      if (response.ok) {
        const blob = await response.blob(); // Convert the response to a Blob
        const reader = new FileReader();
        return new Promise((resolve, reject) => {
          reader.onloadend = () => {
            // Return the Base64-encoded string (exclude the data URL prefix part)
            resolve(reader.result.split(',')[1]);
          };
          reader.onerror = reject;
          reader.readAsDataURL(blob); // Read the Blob as a Data URL (Base64)
        });
      } else {
        console.error("Failed to fetch image:", response.statusText);
        return null;
      }
    } catch (error) {
      console.error("Error fetching image:", error);
      return null;
    }
  };



  const columns = React.useMemo(
    () => [
      {
        Header: "Incident ID",
        accessor: "incident_seq_id",
        Cell: (props) => {
          return (
            <span
              style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
              onClick={() => onRowClick(props.row.original.id)}
              title={props.value || "NA"}
            >
              {props.value || "NA"}
            </span>
          );
        },
        disableSortBy: true,
        leftAlign: true,
      },
      {
        Header: t("Incident Name"),
        accessor: "name",
        Cell: (props) => {
          if (props.value) {
            let categoryText = props.value;
            if (props.value.length > 15) {
              categoryText = props.value.substring(0, 15) + "...";
            }
            return <span title={props.value}>{categoryText}</span>;
          } else {
            return "NA";
          }
        },
        disableSortBy: true,
        leftAlign: true,
      },
      {
        Header: t("Incident Category"),
        accessor: "category",
        Cell: (props) => {
          if (props.value) {
            let name = props.value;
            if (props.value.length > 15) {
              name = props.value.substring(0, 15) + "...";
            }
            return <span title={props.value}>{name}</span>;
          } else {
            return "NA";
          }
        },
        disableSortBy: true,
        leftAlign: true,
      },

       {
        Header: t("Severity"),
        accessor: "severity",
        disableSortBy: true,
        leftAlign: true,
      },

      {
        Header: t("Current Stage"),
        accessor: "current_stage",
        Cell: (props) => {
          const stage = props.value;
          const status = props.row.original.current_status;
          if (!stage && !status) return "NA";
          const stageColors = getStageColors(stage);
          const statusColors = getStageColors(status);
          return (
            <div style={{ display: "flex", flexDirection: "column", gap: "4px", padding: "4px 0" }}>
              {stage && (
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    width: "fit-content",
                    padding: "3px 10px",
                    borderRadius: "999px",
                    background: stageColors.bg,
                    color: stageColors.color,
                    fontSize: "11px",
                    fontWeight: 700,
                    letterSpacing: "0.3px",
                  }}
                >
                  <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: stageColors.dot, flexShrink: 0 }} />
                  {stage.replace(/_/g, " ").toUpperCase()}
                </span>
              )}
              {status && (
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    width: "fit-content",
                    padding: "2px 9px",
                    borderRadius: "999px",
                    background: statusColors.bg,
                    color: statusColors.color,
                    fontSize: "10px",
                    fontWeight: 700,
                    letterSpacing: "0.2px",
                  }}
                >
                  {status}
                </span>
              )}
            </div>
          );
        },
        disableSortBy: true,
        leftAlign: true,
      },
      {
        Header: t("Incident Date & Time"),
        accessor: "created_on",
        Cell: (props) => {
          return props.value
            ? <Moment format="DD-MM-YYYY hh:mm A">{props.value}</Moment>
            : "NA";
        },
        disableSortBy: true,
        leftAlign: true,
      }
      ,



      {
        Header: t("masterresourcetableheaders.action"),
        disableSortBy: true,

        Cell: (props) => {
          const [isdelete, setIsdelete] = useState(false);
          const [selectedId, setSelectedId] = useState(null);

          const [username, setusername] = useState("")
          const getUserdataprof = async () => {
            try {
              const usr_prof = await getData(getThemeURL);
              setusername(usr_prof[0].user_name)

            } catch (error) {

            }
          }
          const deletePopUp = (id) => {
            getUserdataprof()
            setSelectedId(id);
            setIsdelete(!isdelete);
          };
          const deleteTableDataById = async () => {
            try {
              const req = {
                incidentId: selectedId, userId: username
              }
              const response = await postDataWithBody(deleteincident, req, {});
              if (response?.status === "success") {
                await getMasterResourceList();;
                toast.success(
                  response.msg || t("delete_success_message"),
                  {
                    position: toast.POSITION.TOP_CENTER,
                    autoClose: 2000,
                  }
                );
              }
            } catch (error) {
              toast.error(t("delete_error_message"), {
                position: toast.POSITION.TOP_CENTER,
                autoClose: 2000,
              });
              console.error("Delete error:", error);
            } finally {
              setIsdelete(false);
              setSelectedId(null);
            }
          };


          const handleDelete = () => {
            // getUserdataprof()
            deleteTableDataById();
            //deleteTableDataImageByName();
          };


          return (
            <div className="usertable__actionsicon">

              <span
                title="View Incident"
                onClick={() =>
                  navigate("/Disaster-acknowledgereport/situation-awareness/view-incident", {
                    state: { data: props.row.original },
                  })
                }
              >
                <Viewicon value={props.value} className="cursor-pointer me-2" />
              </span>

              <span
                title="View GIS Data"
                onClick={() => {
                  // Log the image name
                  navigate("/Disaster-acknowledgereport/situation-awareness", {
                    state: {
                      id: props.row.original.id,
                      // imgName: props.row.original.resource_image_name, // Pass the image name in the state
                      tabvv: true
                    },
                  });
                }}
              >
                <MapIcon value={props.value} className="cursor-pointer me-2" />
              </span>
              {/* {roleName === process.env.REACT_APP_SUPER_ADMIN && (
              <span
                onClick={() => {
                  // Log the image name
                  navigate("/situation-awareness/create-incidentpage", {
                    state: {
                      id: props.row.original.id,
                      // imgName: props.row.original.resource_image_name, // Pass the image name in the state
                      edit: true
                    },
                  });
                }}
              >
                <Editicon value={props.value} className="cursor-pointer me-2" />
              </span>
              )} */}

              {roleName === process.env.REACT_APP_SUPER_ADMIN && (
                <span title="Delete Incident" onClick={() => deletePopUp(props.row.original.id)}>
                  <Deleteicon
                    value={props.value}
                    className="cursor-pointer mr-1"
                  />
                </span>
              )}

              {isdelete && (
                <Confirmpopup
                  show={isdelete}
                  title={t("deletepopuptext")}
                  message={t("deletepopupmessage")}
                  handleToggle={deletePopUp}
                  row={props.row}
                  name={props.row.original.activity}
                  deleteRow={handleDelete}
                  cancel={t("cancelbutton")}
                  action={t("actionbutton")}
                />
              )}
            </div>
          );
        },
      },
    ],
    [t, onRowClick, selectedRowId, roleName]
  );

  const kpiValues = React.useMemo(
    () => INCIDENT_KPI_CARDS.map((card) => ({ ...card, value: card.compute(data) })),
    [data]
  );

  // Fixed/canonical lists (same values Incident Category Matrix offers) so
  // every configured category/severity is always selectable, even before
  // any incident of that type has been reported yet.
  const severityOptions = SEVERITY_OPTIONS;
  const categoryOptions = masterCategoryOptions;
  // Fixed/canonical lifecycle stages — always offered regardless of which
  // stages the currently-loaded incidents happen to be in.
  const stageOptions = STAGE_OPTIONS;

  const filteredData = React.useMemo(() => {
    const term = searchText.trim().toLowerCase();
    return data.filter((row) => {
      if (severityFilter !== "All" && row.severity !== severityFilter) return false;
      if (categoryFilter !== "All" && row.category !== categoryFilter) return false;
      if (stageFilter !== "All" && row.current_stage !== stageFilter) return false;
      if (term) {
        // Every visible list column (Incident ID, Name, Category, Severity,
        // Stage, Date & Time) so the search box actually matches what's on screen.
        const haystack = [
          row.incident_seq_id,
          row.name,
          row.category,
          row.description,
          row.severity,
          row.current_stage,
          row.created_on ? formatSearchableDate(row.created_on) : null,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      return true;
    });
  }, [data, searchText, severityFilter, categoryFilter, stageFilter]);

  // "Incident Closed By" details — same status_id 954 lookup ViewIncident's
  // ClosedByCard uses — merged in for the export only (the table itself
  // doesn't show these columns).
  const incidentExportData = React.useMemo(
    () =>
      filteredData.map((incident) => {
        // Same field the View Incident screen shows as "Disaster Alert ID" —
        // only nested under sourceInfo in the raw API response, so flatten it.
        const disasterAlertId =
          incident.disaster_notification_seq_id || incident.sourceInfo?.disaster_notification_seq_id || "NA";

        const history = incident.incidentHistoryStatus;
        const closeEntries = Array.isArray(history)
          ? history.filter((h) => h.status_id === 954)
          : [];
        if (closeEntries.length === 0) return { ...incident, disaster_notification_seq_id: disasterAlertId };
        const closedBy = closeEntries.reduce((acc, h) => {
          const t = h.created_on ? new Date(h.created_on.replace(" ", "T")) : null;
          return !acc || (t && t > acc.t) ? { ...h, t } : acc;
        }, null);
        return {
          ...incident,
          disaster_notification_seq_id: disasterAlertId,
          closed_by: [closedBy.first_name, closedBy.last_name].filter(Boolean).join(" ") || "NA",
          closed_by_email: closedBy.created_by || "NA",
          closed_by_mobile: closedBy.mobile_number || "NA",
          closed_on: closedBy.created_on || "NA",
        };
      }),
    [filteredData]
  );

  const hasActiveFilters =
    searchText !== "" || severityFilter !== "All" || categoryFilter !== "All" || stageFilter !== "All";
  const clearFilters = () => {
    setSearchText("");
    setSeverityFilter("All");
    setCategoryFilter("All");
    setStageFilter("All");
  };

  return (
    <div className="create-user" style={{ position: "relative", background: "#fff", padding: "10px", borderRadius: "10px" }}>
      <Loader
        show={loader}
        type="body"
        stack="vertical"
        message="Loading Data"
      />

      {/* KPI cards — uniform row, Total Incidents just keeps a color accent */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${kpiValues.length}, 1fr)`,
          gap: "10px",
          marginBottom: "14px",
        }}
      >
        {kpiValues.map(({ key, label, icon: Icon, bg, color, value }, idx) => {
          const isHero = idx === 0;
          return (
            <div
              key={key}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                padding: "8px 12px",
                borderRadius: "10px",
                border: isHero ? "none" : "1px solid #EEF1F5",
                background: isHero ? "linear-gradient(135deg, #2E6DA4 0%, #3B82C4 100%)" : "#fff",
                boxShadow: isHero ? "0 2px 6px rgba(46,109,164,0.2)" : "0 1px 2px rgba(16,24,40,0.04)",
              }}
            >
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "30px",
                  height: "30px",
                  borderRadius: "50%",
                  background: isHero ? "rgba(255,255,255,0.18)" : bg,
                  flexShrink: 0,
                }}
              >
                <Icon color={isHero ? "#fff" : color} width={15} height={15} />
              </span>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <span style={{ fontSize: "15px", fontWeight: 800, color: isHero ? "#fff" : "#101828", lineHeight: 1.1 }}>
                  {value}
                </span>
                <span
                  style={{
                    ...filterLabelStyle,
                    fontSize: "9.5px",
                    marginTop: "2px",
                    color: isHero ? "rgba(255,255,255,0.85)" : filterLabelStyle.color,
                  }}
                >
                  {label}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Filters card */}
      <div
        style={{
          background: "#fff",
          border: "1px solid #EEF1F5",
          borderRadius: "10px",
          boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
          padding: "10px 14px",
          marginBottom: "12px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px" }}>
          <span
            style={{
              width: "20px",
              height: "20px",
              borderRadius: "6px",
              background: "#EBF4FA",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <FilterIcon size={11} color="#2E6DA4" />
          </span>
          <span style={{ fontSize: "12px", fontWeight: 800, color: "#2E6DA4" }}>Filters</span>
        </div>
        <div style={{ display: "flex", gap: "14px", flexWrap: "wrap", alignItems: "flex-end" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "4px", flex: "1.2 1 240px" }}>
            <label style={filterLabelStyle}>Search</label>
            <input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search by Incident ID, Name, Category, Severity, Stage, Date..."
              title="Search by Incident ID, Name, Category, Description, Severity, Stage, Date & Time"
              style={{ ...filterControlStyle, width: "100%" }}
            />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "4px", flex: "1 1 200px" }}>
            <label style={filterLabelStyle}>Severity</label>
            <select
              value={severityFilter}
              onChange={(e) => setSeverityFilter(e.target.value)}
              style={{ ...filterControlStyle, width: "100%" }}
            >
              <option value="All">Severity</option>
              {severityOptions.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "4px", flex: "1 1 200px" }}>
            <label style={filterLabelStyle}>Incident Category</label>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              style={{ ...filterControlStyle, width: "100%" }}
            >
              <option value="All">Incident Category</option>
              {categoryOptions.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "4px", flex: "1 1 200px" }}>
            <label style={filterLabelStyle}>Stage</label>
            <select
              value={stageFilter}
              onChange={(e) => setStageFilter(e.target.value)}
              style={{ ...filterControlStyle, width: "100%" }}
            >
              <option value="All">Stages</option>
              {stageOptions.map((s) => (
                <option key={s} value={s}>{STAGE_LABELS[s] || toSentenceCase(s)}</option>
              ))}
            </select>
          </div>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              style={{
                padding: "6px 12px",
                borderRadius: "8px",
                border: "1px solid #E4E7EC",
                background: "#F9FAFB",
                color: "#475467",
                fontSize: "12px",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* Table card */}
      <style>{`
        .incident-mgmt-table tbody tr td,
        .incident-mgmt-table tbody tr th {
          border: none !important;
        }
        .incident-mgmt-table tbody tr td {
          background-color: #fff !important;
          color: #1D2939 !important;
        }
        .incident-mgmt-table tbody tr:nth-child(even) td {
          background-color: #F9FAFB !important;
        }
        .incident-mgmt-table tbody tr.incident-row-linked td {
          background-color: #e2e8f0 !important;
          color: #1e293b !important;
          font-weight: 700 !important;
        }
        .incident-mgmt-table tbody tr.incident-row-linked {
          border-left: 3px solid #475569;
        }
      `}</style>
      <div
        className="datatable__container incident-mgmt-table"
        style={{
          position: "relative",
          background: "#fff",
          border: "1px solid #EEF1F5",
          borderRadius: "6px",
          boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
          overflow: "hidden",
        }}
      >
        <div style={{ position: "absolute", top: "13px", right: "32px", zIndex: 10 }}>
          <ExportExcelButton
            exportData={incidentExportData}
            exportColumns={incidentExportColumns}
            exportFileName="Incident_List"
          />
        </div>
        {data && (
          <IncidentShowTableData
            data={filteredData}
            addButtonLabel={t("Create Incident")}
            addNewHandler={addNewHandler}
            onRowClick={onRowClick}
            selectedRowId={selectedRowId}
            selectedIncidentSeqId={selectedIncidentSeqId}
            setSearchData={setSearchData}
            columns={columns}
            numberOfRows={10}
            rowCount={filteredData.length}
            showExport={false}
            showFilter={false}
            showReset={false}
            onExpand={false}
            headerTableHeadStyle={{ background: "linear-gradient(120deg, #2E6DA4 0%, #3B82C4 100%)", color: "#FFFFFF" }}
            tableStyle={{
              border: "none",
              borderRadius:"6px"
            }}
          />
        )}
      </div>
    </div>
  );
};

export default IncidentTable;
